import { Typography } from "@mui/material";
import { ActionButton } from "components/primitives/ActionBarButton";
import { useCountdown } from "hooks/timers/useCountdown";
import { type Ref, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";

export type BackToHomeButtonHandle = {
	restart: () => void;
};

type BackToHomeButtonProps = {
	finishOrder: () => void;
	seconds?: number;
	labelKey?: "order.step5.backToHome" | "order.refillSummary.confirm";
	// Seconds the countdown waits at `seconds` after `restart()` before it runs again.
	holdSeconds?: number;
	ref?: Ref<BackToHomeButtonHandle>;
};

/**
 * Timer button, that redirects user from Order step 5 to Order step 1 after
 * purchase is completed; the refill summary reuses it with its own label and a longer window,
 * and restarts it through `ref` while someone is using the summary
 * @param finishOrder
 * @param seconds
 * @param labelKey
 * @param holdSeconds
 * @param ref
 * @constructor
 */
export const BackToHomeButton = ({
	finishOrder,
	seconds = 10,
	labelKey = "order.step5.backToHome",
	holdSeconds = 0,
	ref,
}: BackToHomeButtonProps) => {
	const { countdown, resetCountdown } = useCountdown(seconds, finishOrder);
	const { t } = useTranslation();

	useImperativeHandle(ref, () => ({ restart: () => resetCountdown(true, holdSeconds) }), [resetCountdown, holdSeconds]);

	if (countdown > 0) {
		return (
			// `data-seconds` carries the number for tests: the shared i18n mock returns the raw key.
			<ActionButton
				onClick={() => finishOrder()}
				stretch={true}
				data-testid="back-to-home-button"
				data-seconds={String(countdown)}
			>
				<Typography variant="h5" color="white" fontWeight={900} textTransform="uppercase">
					{t(labelKey, { countdown: countdown })}
				</Typography>
			</ActionButton>
		);
	}
	return null;
};
